import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './FindCandidates.css';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1').replace('/api/v1', '');

const FindCandidates = ({ apiEndpoint = '/employer/candidates' }) => {
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const fetchCandidates = useCallback(async (targetPage = page) => {
    try {
      setLoading(true);
      const params = { page: targetPage, limit: 12 };
      if (keyword.trim()) params.search = keyword.trim();
      if (location.trim()) params.location = location.trim();

      const response = await api.get(apiEndpoint, { params });
      const data = response.data || {};
      setCandidates(data.candidates || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error(error?.error?.message || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [keyword, location, page]);

  useEffect(() => {
    fetchCandidates(1);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCandidates(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchCandidates(newPage);
  };

  const getResumeUrl = (resume) => {
    if (!resume) return '#';
    if (resume.fileUrl?.startsWith('http')) return resume.fileUrl;
    return `${API_BASE_URL}${resume.fileUrl}`;
  };

  const handleViewProfile = async (candidate) => {
    try {
      await api.post(`${apiEndpoint}/${candidate._id}/view`);
    } catch (error) {
      console.error('Error tracking profile view:', error);
    }
    setSelectedCandidate(candidate);
  };

  const handleViewResume = async (candidateId) => {
    try {
      const response = await api.get(`${apiEndpoint}/${candidateId}/resume`);
      const fileUrl = response.data?.fileUrl;
      if (fileUrl) {
        const url = getResumeUrl({ fileUrl });
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error tracking resume view:', error);
      toast.error('Could not open resume');
    }
  };

  const getPhoneDisplay = (phoneCode, phone) => {
    if (!phone) return 'Not available';
    return `${phoneCode || ''} ${phone}`;
  };

  const getInitials = (name) => name?.charAt(0).toUpperCase() || '?';

  return (
    <div className="fc-page">
      <div className="fc-header">
        <div>
          <h1 className="fc-title">Find Top Talent</h1>
          <p className="fc-subtitle">Search skilled professionals by skill, role, or location</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="fc-search-bar">
        <div className="fc-search-field">
          <span className="material-symbols-outlined fc-search-icon">search</span>
          <input
            type="text"
            placeholder="Skill or role, e.g. Node.js, React Developer..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="fc-input"
          />
        </div>
        <div className="fc-search-field">
          <span className="material-symbols-outlined fc-search-icon">location_on</span>
          <input
            type="text"
            placeholder="City or location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="fc-input"
          />
        </div>
        <button type="submit" className="fc-search-btn" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {candidates.length > 0 && (
        <div className="fc-results-count">
          Found <span>{total}</span> candidate{total !== 1 ? 's' : ''}
        </div>
      )}

      {loading && candidates.length === 0 ? (
        <div className="fc-loading">
          <div className="fc-spinner"></div>
          <p>Loading candidates…</p>
        </div>
      ) : candidates.length === 0 ? (
        <div className="fc-empty">
          <span className="material-symbols-outlined">search_off</span>
          <h3>No candidates found</h3>
          <p>Try a different skill or location</p>
        </div>
      ) : (
        <>
          <div className="fc-list">
            {candidates.map((c) => (
              <div key={c._id} className="fc-list-item">
                <div className="fc-list-col">
                  <span className="fc-list-label">Name</span>
                  <span className="fc-list-value fc-list-name">{c.name}</span>
                </div>
                <div className="fc-list-col">
                  <span className="fc-list-label">Designation</span>
                  <span className="fc-list-value">{c.desiredJobTitle || c.headline || 'Open to Opportunities'}</span>
                </div>
                <div className="fc-list-col">
                  <span className="fc-list-label">Mobile</span>
                  <span className="fc-list-value fc-list-mono">{getPhoneDisplay(c.phoneCode, c.phone)}</span>
                </div>
                <div className="fc-list-col">
                  <span className="fc-list-label">Experience</span>
                  <span className="fc-list-value">
                    {c.experience?.years > 0
                      ? `${c.experience.years} yrs`
                      : 'Fresher'}
                  </span>
                </div>
                <div className="fc-list-col fc-list-action">
                  <button
                    onClick={() => handleViewProfile(c)}
                    className="cst-deafult-btn"
                  >
                    <span className="material-symbols-outlined">open_in_new</span> View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="fc-pagination">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`fc-page-btn ${page === p ? 'active' : ''}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selectedCandidate && (
        <div className="fc-modal-overlay" onClick={() => setSelectedCandidate(null)}>
          <div className="fc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="fc-modal-close" onClick={() => setSelectedCandidate(null)}>
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="fc-modal-header">
              <div className="fc-modal-avatar">
                {selectedCandidate.profilePicture ? (
                  <img src={selectedCandidate.profilePicture} alt={selectedCandidate.name} />
                ) : (
                  <span>{getInitials(selectedCandidate.name)}</span>
                )}
              </div>
              <div className="fc-modal-info">
                <h3>{selectedCandidate.name}</h3>
                <p className="fc-modal-headline">
                  {selectedCandidate.desiredJobTitle || selectedCandidate.headline || 'Open to Opportunities'}
                </p>
                {selectedCandidate.location?.city && (
                  <span className="fc-modal-location">
                    <span className="material-symbols-outlined">location_on</span>
                    {selectedCandidate.location.city}
                  </span>
                )}
              </div>
            </div>

            <div className="fc-modal-contact">
              <div className="fc-modal-contact-item">
                <span className="material-symbols-outlined">phone</span>
                <span>{getPhoneDisplay(selectedCandidate.phoneCode, selectedCandidate.phone)}</span>
              </div>
              {selectedCandidate.email && (
                <div className="fc-modal-contact-item">
                  <span className="material-symbols-outlined">email</span>
                  <span>{selectedCandidate.email}</span>
                </div>
              )}
            </div>

            <div className="fc-modal-section">
              <h4>Basic Info</h4>
              <div className="fc-modal-meta">
                {selectedCandidate.dateOfBirth && (
                  <div>
                    <span className="fc-modal-meta-label">Date of Birth</span>
                    <span>{new Date(selectedCandidate.dateOfBirth).toLocaleDateString()}</span>
                  </div>
                )}
                {selectedCandidate.gender && (
                  <div>
                    <span className="fc-modal-meta-label">Gender</span>
                    <span>{selectedCandidate.gender}</span>
                  </div>
                )}
                {selectedCandidate.currentEmploymentStatus && (
                  <div>
                    <span className="fc-modal-meta-label">Employment Status</span>
                    <span>{selectedCandidate.currentEmploymentStatus}</span>
                  </div>
                )}
                {selectedCandidate.willingToRelocate !== undefined && (
                  <div>
                    <span className="fc-modal-meta-label">Willing to Relocate</span>
                    <span>{selectedCandidate.willingToRelocate ? 'Yes' : 'No'}</span>
                  </div>
                )}
                {selectedCandidate.alternateEmail && (
                  <div>
                    <span className="fc-modal-meta-label">Alternate Email</span>
                    <span>{selectedCandidate.alternateEmail}</span>
                  </div>
                )}
                {selectedCandidate.alternatePhone && (
                  <div>
                    <span className="fc-modal-meta-label">Alternate Phone</span>
                    <span>{selectedCandidate.alternatePhone}</span>
                  </div>
                )}
                {selectedCandidate.linkedIn && (
                  <div>
                    <span className="fc-modal-meta-label">LinkedIn</span>
                    <span>{selectedCandidate.linkedIn}</span>
                  </div>
                )}
                {selectedCandidate.github && (
                  <div>
                    <span className="fc-modal-meta-label">GitHub</span>
                    <span>{selectedCandidate.github}</span>
                  </div>
                )}
                {selectedCandidate.portfolio && (
                  <div>
                    <span className="fc-modal-meta-label">Portfolio</span>
                    <span>{selectedCandidate.portfolio}</span>
                  </div>
                )}
                {selectedCandidate.website && (
                  <div>
                    <span className="fc-modal-meta-label">Website</span>
                    <span>{selectedCandidate.website}</span>
                  </div>
                )}
              </div>
            </div>

            {selectedCandidate.summary && (
              <div className="fc-modal-section">
                <h4>Summary</h4>
                <p>{selectedCandidate.summary}</p>
              </div>
            )}

            {selectedCandidate.skillsDetailed?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Skills</h4>
                <div className="fc-modal-skills">
                  {selectedCandidate.skillsDetailed.map((skill, i) => (
                    <span key={i} className="fc-skill-tag">{skill.name} {skill.proficiency ? `(${skill.proficiency})` : ''}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="fc-modal-section">
              <h4>Experience & Salary</h4>
              <div className="fc-modal-meta">
                <div>
                  <span className="fc-modal-meta-label">Experience</span>
                  <span>
                    {selectedCandidate.experience?.years > 0
                      ? `${selectedCandidate.experience.years} yrs ${selectedCandidate.experience.months > 0 ? `${selectedCandidate.experience.months} mo` : ''}`
                      : 'Fresher'}
                  </span>
                </div>
                {selectedCandidate.expectedSalary && (
                  <div>
                    <span className="fc-modal-meta-label">Expected Salary</span>
                    <span>
                      {selectedCandidate.expectedSalary.currency || 'INR'} {selectedCandidate.expectedSalary.min || 0} - {selectedCandidate.expectedSalary.max || 0}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {selectedCandidate.workExperience?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Work Experience</h4>
                <div className="fc-modal-list">
                  {selectedCandidate.workExperience.map((exp, i) => (
                    <div key={i} className="fc-modal-list-item">
                      <div className="fc-modal-list-title">{exp.designation}</div>
                      <div className="fc-modal-list-subtitle">{exp.company}</div>
                      <div className="fc-modal-list-meta">
                        {exp.employmentType} {exp.location ? `• ${exp.location}` : ''}
                        {exp.currentlyWorking ? ' • Current' : exp.endDate ? ` • ${new Date(exp.endDate).getFullYear()}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCandidate.education?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Education</h4>
                <div className="fc-modal-list">
                  {selectedCandidate.education.map((edu, i) => (
                    <div key={i} className="fc-modal-list-item">
                      <div className="fc-modal-list-title">{edu.degree}</div>
                      <div className="fc-modal-list-subtitle">{edu.institution}</div>
                      <div className="fc-modal-list-meta">
                        {edu.fieldOfStudy} {edu.startYear ? `• ${edu.startYear}-${edu.endYear || 'Present'}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCandidate.certifications?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Certifications</h4>
                <div className="fc-modal-list">
                  {selectedCandidate.certifications.map((cert, i) => (
                    <div key={i} className="fc-modal-list-item">
                      <div className="fc-modal-list-title">{cert.name}</div>
                      <div className="fc-modal-list-subtitle">{cert.issuingOrganization}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCandidate.projects?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Projects</h4>
                <div className="fc-modal-list">
                  {selectedCandidate.projects.map((proj, i) => (
                    <div key={i} className="fc-modal-list-item">
                      <div className="fc-modal-list-title">{proj.title}</div>
                      <div className="fc-modal-list-subtitle">{proj.role}</div>
                      {proj.technologies?.length > 0 && (
                        <div className="fc-modal-list-meta">{proj.technologies.join(', ')}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCandidate.languages?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Languages</h4>
                <div className="fc-modal-skills">
                  {selectedCandidate.languages.map((lang, i) => (
                    <span key={i} className="fc-skill-tag">{lang.language} ({lang.proficiency})</span>
                  ))}
                </div>
              </div>
            )}

            {selectedCandidate.jobPreferences && (
              <div className="fc-modal-section">
                <h4>Job Preferences</h4>
                <div className="fc-modal-meta">
                  {selectedCandidate.jobPreferences.desiredJobTitle && (
                    <div>
                      <span className="fc-modal-meta-label">Desired Role</span>
                      <span>{selectedCandidate.jobPreferences.desiredJobTitle}</span>
                    </div>
                  )}
                  {selectedCandidate.jobPreferences.desiredJobTypes?.length > 0 && (
                    <div>
                      <span className="fc-modal-meta-label">Job Types</span>
                      <span>{selectedCandidate.jobPreferences.desiredJobTypes.join(', ')}</span>
                    </div>
                  )}
                  {selectedCandidate.jobPreferences.workMode?.length > 0 && (
                    <div>
                      <span className="fc-modal-meta-label">Work Mode</span>
                      <span>{selectedCandidate.jobPreferences.workMode.join(', ')}</span>
                    </div>
                  )}
                  {selectedCandidate.jobPreferences.noticePeriod && (
                    <div>
                      <span className="fc-modal-meta-label">Notice Period</span>
                      <span>{selectedCandidate.jobPreferences.noticePeriod.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {selectedCandidate.jobPreferences.expectedSalary?.max > 0 && (
                    <div>
                      <span className="fc-modal-meta-label">Expected Salary</span>
                      <span>{selectedCandidate.jobPreferences.expectedSalary.currency || 'INR'} {selectedCandidate.jobPreferences.expectedSalary.min || 0} - {selectedCandidate.jobPreferences.expectedSalary.max}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedCandidate.resumes?.length > 0 && (
              <div className="fc-modal-section">
                <h4>Resumes</h4>
                <div className="fc-modal-resumes">
                  {selectedCandidate.resumes.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => handleViewResume(selectedCandidate._id)}
                      className="fc-modal-resume-link"
                    >
                      <span className="material-symbols-outlined">description</span>
                      View Resume {selectedCandidate.resumes.length > 1 ? `#${i + 1}` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FindCandidates;
